/**
 * Time and sales.
 *
 * Split out of `OrderBook` because it is a separate product from depth and a
 * separate panel: prints are Level 1, which a trader with ordinary real-time
 * quotes already has, while the book behind them is a further exchange
 * subscription they may hold no right to at all. A tape that lived inside the
 * DOM panel would be refused whenever depth was, which would be wrong.
 *
 * ## Colour is the aggressor, and it is never guessed
 *
 * A print between the quotes genuinely cannot be classified — the venue did
 * not say, or it traded at the midpoint — and is drawn neutral. Traders read
 * that colour as a fact about who paid, so a guess here is a lie in a colour
 * they trust.
 */
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { TapeResponse } from '@aegis/api-client';
import { ApiError } from '@aegis/api-client';
import { api } from '../lib/api.js';

const REFRESH_MS = 1500;

export function Tape({
  symbol, precision, live, limit = 40,
}: {
  symbol: string;
  precision: number;
  /** False during replay — see `OrderBook`. Same reason, same wording. */
  live: boolean;
  limit?: number;
}): JSX.Element {
  const [tape, setTape] = useState<TapeResponse | null>(null);
  const [minSize, setMinSize] = useState(0);
  const [refusal, setRefusal] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setRefusal(null);
    /* No symbol means no request. `GET /v1/tape/` — with nothing after the
       slash — is a different route entirely, and the 404 it answers looks in
       the log exactly like a symbol the feed does not carry. The screen has no
       symbol for a moment on first paint and whenever the instrument master is
       still loading, which is often enough to fill a log with them. */
    if (!live || !symbol) { setTape(null); return undefined; }

    const poll = async (): Promise<void> => {
      try {
        setTape(await api.tape(symbol, limit, minSize));
        setRefusal(null);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          setTape(null);
          setRefusal(e.message);
          // Stop polling a refusal. Retrying a licensing decision on a timer
          // achieves nothing and fills the log.
          if (timer.current) { clearInterval(timer.current); timer.current = null; }
        }
        // A transient failure is left alone — the next poll fixes it.
      }
    };

    void poll();
    timer.current = setInterval(() => { void poll(); }, REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [symbol, limit, minSize, live]);

  const px = (n: number): string => n.toFixed(precision);

  return (
    <section className="tape-panel" aria-label="Time and sales">
      <div className="tape-head">
        <h4>Time &amp; sales</h4>
        <label className="dim">
          min size
          <input
            type="number" min={0} max={999} value={minSize}
            onChange={(e) => setMinSize(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
      </div>

      {!live && (
        <p className="panel-empty">
          Hidden during replay. The tape is live and the chart is not — showing
          them together is the confusion replay exists to prevent.
        </p>
      )}

      {live && !symbol && (
        <p className="panel-empty">Pick an instrument to see its prints.</p>
      )}

      {refusal && <div className="dom-refusal"><p>{refusal}</p></div>}

      {live && symbol && !refusal && (
        <ol className="tape">
          {(tape?.prints ?? []).map((p, i) => (
            <li key={`${p.at}-${i}`} className={`print ${p.aggressor}`}>
              <span className="mono">{new Date(p.at).toLocaleTimeString(undefined, {
                hour12: false, minute: '2-digit', second: '2-digit',
              })}</span>
              <span className="mono">{px(p.price)}</span>
              <span className="mono">{p.size}</span>
            </li>
          ))}
          {(tape?.prints.length ?? 0) === 0 && (
            <li className="dim">
              {minSize > 0 ? `No prints at ${minSize} or above yet.` : 'No prints yet.'}
            </li>
          )}
        </ol>
      )}

      <p className="field-hint">
        Colour marks the side that lifted or hit. A print between the quotes is
        left uncoloured because it genuinely cannot be classified.
      </p>
    </section>
  );
}
